import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PageMeta from '../components/PageMeta';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { PLAN_RANK } from '../lib/planLimits';
import { isNativePlatform } from '../lib/nativeSocialAuth';
import { usePaywall } from '../context/PaywallContext';
import { getCurrentOffering } from '../lib/purchases';
import { planPricesFromOffering, webPlanPrices, WEB_PLUS_PRICES } from '../lib/storePricing';

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    period: 'forever',
    description: 'Your whole financial picture, with AI-powered insights.',
    features: [
      'Plaid bank connection',
      'Expenses, Budgets, Goals',
      'Connect your brokerage — portfolio view + manual trading',
      'AI Insights: banking agents, 1 refresh/day',
      'AI Chat: 10 messages/day',
      'Join a household',
    ],
    cta: 'Get started free',
    ctaTo: '/register',
  },
  {
    id: 'plus',
    name: 'Plus',
    description: 'Full AI across your finances and investments, plus automation tooling.',
    features: [
      'Everything in Free',
      'AI Insights: all 12 agents, 3 refreshes/day',
      'AI Chat: 30 messages/day',
      'Trading automations — full rules + universe scan, on paper or your live brokerage account (notify-only: you confirm each trade)',
      'Multiple linked accounts',
      'Create a household dashboard',
    ],
    cta: 'Start 7-day free trial',
    highlight: true,
  },
];


export default function Pricing() {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(null);
  const { openPaywall, canPurchaseInApp } = usePaywall();
  const [error, setError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [trialUsed, setTrialUsed] = useState(false);
  const [billing, setBilling] = useState('monthly');
  // Native: prices come from the store (undefined = loading, null = unavailable). Web: Stripe's
  // USD constants. Never a hardcoded figure on native — see lib/storePricing.js.
  const native = isNativePlatform();
  const [storePrices, setStorePrices] = useState(undefined);
  useEffect(() => {
    if (!native) return undefined;
    if (!canPurchaseInApp) { setStorePrices(null); return undefined; }
    let cancelled = false;
    getCurrentOffering().then(o => { if (!cancelled) setStorePrices(planPricesFromOffering(o)); });
    return () => { cancelled = true; };
  }, [native, canPurchaseInApp]);
  const prices = native ? storePrices : webPlanPrices();
  // Stripe always offers the 7-day trial on web; on native only when the store reports one.
  const trialOffered = native ? Boolean(prices?.hasFreeTrial) : true;

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/auth/me').then(res => {
      setCurrentPlan(res.data.plan);
      setTrialUsed(res.data.trialUsed || false);
    }).catch(() => {});
  }, [isAuthenticated]);

  const planRank = PLAN_RANK[currentPlan] ?? -1;

  async function handleUpgrade(planId) {
    setLoading(planId);
    setError(null);
    try {
      const res = await api.post('/billing/checkout', { plan: planId });
      if (!res.data.url) throw new Error('No checkout URL returned');
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setLoading(null);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PageMeta titleOverride="Agence Pricing — Personal Finance AI Plans" description="Free and Plus plans for Agence, the AI budget tracker and investment insights app. Try Plus free for 7 days — no card until day 8." path="/pricing" schema={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': 'Agence Pricing',
        'url': 'https://agence.finance/pricing',
        'description': 'Free and Plus plans for Agence, the AI personal finance and investment app.',
        'offers': [
          {
            '@type': 'Offer',
            'name': 'Free',
            'price': '0',
            'priceCurrency': 'USD',
            'description': 'Core AI insights, bank connection, budgets, goals, and portfolio view.',
          },
          {
            '@type': 'Offer',
            'name': 'Plus',
            'price': String(WEB_PLUS_PRICES.monthly),
            'priceCurrency': 'USD',
            'billingIncrement': 'P1M',
            'description': 'Full AI across finances and investments, autopilot paper trading, unlimited chat.',
          },
        ],
        'provider': { '@type': 'Organization', 'name': 'Agence', 'url': 'https://agence.finance' },
      }} />
      <PublicNav />
      <main style={{ flex: 1, padding: '4rem 2rem 2rem', maxWidth: '1100px', margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
            Simple, transparent pricing
          </h1>
          <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', maxWidth: '480px', margin: '0 auto' }}>
            {(isAuthenticated && trialUsed) || !trialOffered
              ? 'Subscribe to Plus and get full AI coverage across your finances.'
              : native
                ? 'Try Plus free before your first charge.'
                : 'Try Plus free for 7 days. No charge until day 8.'}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', background: 'var(--bg-surface)', border: '1px solid var(--navy-200)', borderRadius: '8px', padding: '3px' }}>
              <button
                onClick={() => setBilling('monthly')}
                style={{
                  padding: '0.4rem 1.1rem', minHeight: '44px', borderRadius: '6px', border: 'none', fontFamily: 'inherit',
                  fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                  background: billing === 'monthly' ? 'var(--navy-600)' : 'transparent',
                  color: billing === 'monthly' ? '#fff' : 'var(--text-secondary)',
                  transition: 'background 150ms, color 150ms',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling('annual')}
                style={{
                  padding: '0.4rem 1.1rem', minHeight: '44px', borderRadius: '6px', border: 'none', fontFamily: 'inherit',
                  fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                  background: billing === 'annual' ? 'var(--navy-600)' : 'transparent',
                  color: billing === 'annual' ? '#fff' : 'var(--text-secondary)',
                  display: 'flex', alignItems: 'center', gap: '0.45rem',
                  transition: 'background 150ms, color 150ms',
                }}
              >
                Annual
                {prices?.savePercent && (
                  <span style={{
                    background: 'var(--gain-bg)', color: 'var(--gain)',
                    fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: '4px',
                  }}>
                    Save {prices.savePercent}%
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ background: 'var(--loss-bg)', border: '1px solid var(--loss)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '2rem', color: 'var(--loss)', maxWidth: '600px', margin: '0 auto 2rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', alignItems: 'start' }}>
          {TIERS.map(tier => {
            const isAnnual = billing === 'annual' && tier.id === 'plus';
            const annual = prices?.annual;
            let displayPrice;
            let displayPeriod = tier.period;
            let displayNote = null;
            if (tier.id === 'free') {
              // "$0" beside store prices in another currency is the mismatch to avoid.
              displayPrice = native ? 'Free' : '$0';
            } else if (isAnnual) {
              displayPrice = annual?.perMonthString || annual?.priceString;
              displayPeriod = annual?.perMonthString ? 'per month' : 'per year';
              displayNote = annual?.perMonthString ? `billed ${annual.priceString}/yr` : null;
            } else {
              displayPrice = prices?.monthly?.priceString;
              displayPeriod = 'per month';
            }
            const checkoutPlan = isAnnual ? 'plus_annual' : tier.id;
            return (
            <div
              key={tier.id}
              style={{
                background: tier.highlight ? 'var(--navy-600)' : 'var(--bg-surface)',
                border: tier.highlight ? 'none' : '1px solid var(--navy-200)',
                borderRadius: '12px',
                padding: '2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
                position: 'relative',
              }}
            >
              {tier.highlight && (
                <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--navy-400)', color: '#fff', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', padding: '0.25rem 0.75rem', borderRadius: '20px', textTransform: 'uppercase' }}>
                  Most popular
                </div>
              )}

              <div>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: tier.highlight ? 'var(--pricing-hl-label)' : 'var(--text-muted)', marginBottom: '0.4rem' }}>
                  {tier.name}
                </div>
                {displayPrice ? (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
                    <span style={{ fontSize: '2.25rem', fontWeight: 700, color: tier.highlight ? '#fff' : 'var(--text-primary)' }}>{displayPrice}</span>
                    <span style={{ fontSize: '0.85rem', color: tier.highlight ? 'var(--pricing-hl-period)' : 'var(--text-muted)' }}>{displayPeriod}</span>
                  </div>
                ) : (
                  // No fallback figure: a placeholder price would be USD for every non-US user.
                  <div style={{ fontSize: '0.9rem', color: tier.highlight ? 'var(--pricing-hl-period)' : 'var(--text-muted)', padding: '0.75rem 0' }}>
                    {storePrices === undefined ? 'Loading prices…' : 'Prices load from your app store when you upgrade.'}
                  </div>
                )}
                {displayNote && (
                  <div style={{ fontSize: '0.75rem', color: tier.highlight ? 'var(--pricing-hl-note)' : 'var(--text-muted)', marginTop: '-0.5rem' }}>
                    {displayNote}
                  </div>
                )}
                <p style={{ fontSize: '0.88rem', color: tier.highlight ? 'var(--pricing-hl-desc)' : 'var(--text-secondary)', marginTop: '0.25rem', lineHeight: 1.5 }}>
                  {tier.description}
                </p>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem', flex: 1 }}>
                {tier.features.map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.88rem', color: tier.highlight ? 'var(--pricing-hl-feature)' : 'var(--text-secondary)' }}>
                    <span style={{ color: tier.highlight ? 'var(--pricing-hl-check)' : 'var(--navy-600)', flexShrink: 0, marginTop: '2px' }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              {currentPlan !== null && PLAN_RANK[currentPlan] !== undefined && PLAN_RANK[currentPlan] === PLAN_RANK[tier.id] ? (
                <div style={{
                  display: 'block', textAlign: 'center', padding: '0.7rem 1.25rem',
                  borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem',
                  background: 'transparent',
                  border: tier.highlight ? `1px solid var(--pricing-hl-border-md)` : '1px solid var(--navy-300)',
                  color: tier.highlight ? 'var(--pricing-hl-text-dim)' : 'var(--text-muted)',
                }}>
                  Current plan
                </div>
              ) : planRank > PLAN_RANK[tier.id] ? (
                <div style={{
                  display: 'block', textAlign: 'center', padding: '0.7rem 1.25rem',
                  borderRadius: '8px', fontSize: '0.9rem',
                  background: 'transparent',
                  border: tier.highlight ? `1px solid var(--pricing-hl-border-sm)` : '1px solid var(--navy-200)',
                  color: tier.highlight ? 'var(--pricing-hl-text-xs)' : 'var(--text-muted)',
                }}>
                  Included in your plan
                </div>
              ) : tier.id === 'free' ? (
                <Link
                  to={isAuthenticated ? '/' : '/register'}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                    padding: '0.7rem 1.25rem', minHeight: '44px', boxSizing: 'border-box',
                    borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none',
                    background: 'transparent', border: '1px solid var(--navy-300)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {isAuthenticated ? 'Get started' : tier.cta}
                </Link>
              ) : isNativePlatform() ? (
                // In-app purchase only. This used to email a Stripe checkout link, which
                // is the external purchase CTA Guideline 3.1.1 rejected. With purchases
                // unavailable (missing or Test Store key) it says so rather than falling
                // back to that path.
                canPurchaseInApp ? (
                  <button
                    onClick={openPaywall}
                    style={{
                      padding: '0.7rem 1.25rem', minHeight: '44px', borderRadius: '8px', fontWeight: 600,
                      fontSize: '0.9rem', cursor: 'pointer', border: 'none', fontFamily: 'inherit',
                      background: tier.highlight ? '#fff' : 'var(--navy-600)',
                      color: tier.highlight ? 'var(--navy-600)' : '#fff',
                    }}
                  >
                    {trialOffered && !(isAuthenticated && trialUsed) ? 'Start free trial' : 'Upgrade'}
                  </button>
                ) : (
                  <div style={{
                    display: 'block', textAlign: 'center', padding: '0.7rem 1.25rem',
                    borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem',
                    background: 'transparent',
                    border: tier.highlight ? `1px solid var(--pricing-hl-border-md)` : '1px solid var(--navy-300)',
                    color: tier.highlight ? '#fff' : 'var(--text-primary)',
                  }}>
                    Temporarily unavailable
                  </div>
                )
              ) : (
                <button
                  onClick={() => handleUpgrade(checkoutPlan)}
                  disabled={loading === checkoutPlan}
                  style={{
                    padding: '0.7rem 1.25rem', minHeight: '44px', borderRadius: '8px', fontWeight: 600,
                    fontSize: '0.9rem', cursor: loading === checkoutPlan ? 'not-allowed' : 'pointer',
                    border: 'none', fontFamily: 'inherit',
                    background: tier.highlight ? '#fff' : 'var(--navy-600)',
                    color: tier.highlight ? 'var(--navy-600)' : '#fff',
                    opacity: loading === checkoutPlan ? 0.7 : 1,
                  }}
                >
                  {loading === checkoutPlan
                    ? 'Redirecting…'
                    : (isAuthenticated && trialUsed && tier.id === 'plus')
                      ? 'Subscribe to Plus'
                      : tier.cta}
                </button>
              )}
            </div>
            );
          })}
        </div>

        <p style={{ textAlign: 'center', marginTop: '2.5rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          Cancel or change plans anytime from <Link to="/settings#subscription" style={{ color: 'var(--text-muted)' }}>Account → Subscription</Link>.
          {!native && ' All prices in USD.'}
        </p>
        <p style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          Not sure a budgeting app that also tracks investments is worth it?{' '}
          <a href="/blog/cost-of-separate-budgeting-investing-apps/" style={{ color: 'var(--text-muted)' }}>See what running two separate apps actually costs</a>.
        </p>
      </main>
      <Footer />
    </div>
  );
}
